// src/components/userSettings/VacationSettingsSection.jsx
//
// Vacation Settings (Editable) — Enterprise Safe
// =============================================
//
// This section configures the Leave / HR inputs for one user.
//
// IMPORTANT ARCHITECTURAL NOTE
// ----------------------------
// Leave duration is now resolved by the backend in this priority order:
// 1) facility/company exception calendar,
// 2) employee one-day override,
// 3) exact weekly work schedule,
// 4) assigned rotating work pattern,
// 5) default_daily_minutes fallback for default_working_days.
//
// The UI intentionally exposes both:
// - the old exact weekly schedule editor for simple/static employees,
// - the new rotating work-pattern assignment for employees working A/B weeks.
//
// Balance accounting is profile-aware:
// - backend stores leave duration in minutes,
// - balances display days as minutes / user_leave_profiles.leave_day_minutes,
// - default_daily_minutes is a fallback duration input, NOT the balance-day
//   divisor.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  clearWorkPatternAssignment,
  deleteWorkScheduleOverride,
  fetchVacationSettings,
  previewLeaveDayNormRecalculation,
  recalculateLeaveBalancesAfterDayNormChange,
  setWorkPatternAssignment,
  setWorkSchedule,
  setWorkScheduleOverride,
  upsertLeaveProfile,
} from "../../services/vacationSettingsService";
import { isRequestCanceled } from "../../services/httpUtils";

import VacationBalancesSection from "./VacationBalancesSection";

const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7];

function todayDateOnly() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Parse a date-only string as UTC midnight.
 *
 * Why not plain `new Date("YYYY-MM-DD")` everywhere?
 * JavaScript date parsing is easy to misuse around local time zones.  This
 * helper keeps the assignment-duration calculation strictly date-only, which
 * is exactly what HR users expect when they say "8 weeks from this date".
 */
function parseDateOnlyUtc(value) {
  const text = String(value || "").trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  // Guard against overflow dates such as 2026-02-31.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
}

function formatDateOnlyUtc(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Calculate Effective To from "Effective From + assignment duration in weeks".
 *
 * Business rule:
 * - Effective From is inclusive.
 * - Effective To is inclusive.
 * - Therefore an 8-week assignment starting on Monday 2026-01-05 ends on
 *   Sunday 2026-03-01: start + (8 * 7) - 1 day.
 *
 * This helper does NOT change cycle length.  Cycle length describes the pattern
 * template repeat structure.  Assignment duration describes how long this
 * concrete employee assignment remains valid.
 */
function calculateEffectiveToFromDuration(effectiveFrom, durationWeeks) {
  const start = parseDateOnlyUtc(effectiveFrom);
  const weeks = Number(durationWeeks);

  if (!start || !Number.isInteger(weeks) || weeks <= 0) return "";

  const result = new Date(start.getTime());
  result.setUTCDate(result.getUTCDate() + weeks * 7 - 1);
  return formatDateOnlyUtc(result);
}

function deriveWholeWeekDuration(effectiveFrom, effectiveTo) {
  const start = parseDateOnlyUtc(effectiveFrom);
  const end = parseDateOnlyUtc(effectiveTo);
  if (!start || !end || end < start) return "";

  const diffDaysInclusive = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  if (diffDaysInclusive <= 0 || diffDaysInclusive % 7 !== 0) return "";

  return String(diffDaysInclusive / 7);
}

function normalizePositiveIntegerString(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const n = Number(text);
  if (!Number.isInteger(n) || n <= 0) return text;
  return String(n);
}

function formatMinutesAsHours(minutes) {
  const n = Number(minutes || 0);
  if (!Number.isFinite(n)) return "0.00";
  return (n / 60).toFixed(2);
}

function formatNormHours(minutes) {
  const n = Number(minutes || 480);
  if (!Number.isFinite(n) || n <= 0) return "8.00";
  return (n / 60).toFixed(2);
}

function getLeaveDayNormMode(minutes) {
  const n = Number(minutes || 480);
  if (n === 480) return "480";
  if (n === 420) return "420";
  return "custom";
}

function hoursInputToMinutes(value) {
  const normalized = String(value ?? "").replace(",", ".").trim();
  if (!normalized) return null;

  const hours = Number(normalized);
  if (!Number.isFinite(hours)) return null;

  return Math.round(hours * 60);
}

function formatRecalcValue(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function normalizeScheduleDays(draftSchedule) {
  const days = Array.isArray(draftSchedule?.days) ? draftSchedule.days : [];
  const map = new Map(days.map((d) => [Number(d.day_of_week), Number(d.minutes || 0)]));

  return ISO_DAYS.map((dow) => ({
    day_of_week: dow,
    minutes: Number.isFinite(map.get(dow)) ? Math.trunc(map.get(dow)) : 0,
  }));
}

function normalizeDefaultWorkingDays(value) {
  const raw = String(value || "").trim();
  if (!raw) return "1,2,3,4,5";

  const cleaned = raw
    .split(/[,;\s]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7);

  const unique = Array.from(new Set(cleaned));
  return unique.length ? unique.join(",") : "1,2,3,4,5";
}

function normalizePatternDays(template) {
  const days = Array.isArray(template?.days) ? template.days : [];
  return days
    .map((d) => ({
      week_index: Number(d.week_index),
      day_of_week: Number(d.day_of_week),
      minutes: Number(d.minutes || 0),
    }))
    .filter((d) => d.week_index >= 1 && d.day_of_week >= 1 && d.day_of_week <= 7)
    .sort((a, b) => a.week_index - b.week_index || a.day_of_week - b.day_of_week);
}

export default function VacationSettingsSection({ userId, readOnly = false }) {
  const { t } = useTranslation(["common", "leaves"]);

  const dayLabels = useMemo(
    () => [
      t("leaves:vacationSettings.days.mon"),
      t("leaves:vacationSettings.days.tue"),
      t("leaves:vacationSettings.days.wed"),
      t("leaves:vacationSettings.days.thu"),
      t("leaves:vacationSettings.days.fri"),
      t("leaves:vacationSettings.days.sat"),
      t("leaves:vacationSettings.days.sun"),
    ],
    [t]
  );

  const [bundle, setBundle] = useState(null);

  const [draftProfile, setDraftProfile] = useState({});
  const [draftSchedule, setDraftSchedule] = useState({
    effective_from: null,
    effective_to: null,
    days: [],
  });

  const [overrideForm, setOverrideForm] = useState({
    id: null,
    work_date: todayDateOnly(),
    minutes: 0,
    reason: "",
  });

  const [patternForm, setPatternForm] = useState({
    template_id: "",
    effective_from: todayDateOnly(),
    effective_to: "",
    cycle_start_date: todayDateOnly(),

    // UI-only helper.  This value is not stored in the database.  When filled,
    // the UI calculates effective_to as inclusive end date:
    // effective_from + (assignment_duration_weeks * 7 days) - 1 day.
    assignment_duration_weeks: "",
  });

  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [savingPattern, setSavingPattern] = useState(false);
  const [showLeaveDayNormRecalc, setShowLeaveDayNormRecalc] = useState(false);
  const [recalcYear, setRecalcYear] = useState(String(new Date().getFullYear()));
  const [recalcPreview, setRecalcPreview] = useState(null);
  const [loadingRecalcPreview, setLoadingRecalcPreview] = useState(false);
  const [runningRecalc, setRunningRecalc] = useState(false);

  // UI mode for the leave-day norm selector.
  //
  // The stored backend value is still a single numeric `leave_day_minutes` field.
  // This UI state only controls whether the administrator is currently using one
  // of the common presets (8h/7h) or the custom-hours editor.  We keep this
  // state separate from the numeric value because selecting "custom" while the
  // value is still 480 or 420 must keep the custom input visible even before the
  // user changes the number.
  const [leaveDayNormMode, setLeaveDayNormMode] = useState("480");
  const [customLeaveDayHoursInput, setCustomLeaveDayHoursInput] = useState("8.00");

  // Exact work schedule is now optional.
  //
  // The section remains available as a high-priority dated override, but the
  // user can be fully configured through a rotating work pattern or the
  // default-daily-minutes fallback. Therefore the schedule validation message
  // must not be shown just because no exact schedule exists. We only expose
  // validation after the administrator explicitly tries to save this exact
  // schedule card.
  const [scheduleSaveAttempted, setScheduleSaveAttempted] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isReadOnly = !!readOnly;
  const isBusy = loading || savingProfile || savingSchedule || savingOverride || savingPattern || loadingRecalcPreview || runningRecalc;

  function applyLoadedBundle(data) {
    setBundle(data);
    setDraftProfile(data?.profile || {});

    const loadedLeaveDayMinutes = Number(data?.profile?.leave_day_minutes || 480);
    const safeLoadedLeaveDayMinutes = Number.isFinite(loadedLeaveDayMinutes) && loadedLeaveDayMinutes > 0
      ? Math.trunc(loadedLeaveDayMinutes)
      : 480;

    setLeaveDayNormMode(getLeaveDayNormMode(safeLoadedLeaveDayMinutes));
    setCustomLeaveDayHoursInput(formatNormHours(safeLoadedLeaveDayMinutes));

    setDraftSchedule(data?.workSchedule || { effective_from: null, effective_to: null, days: [] });
    setOverrideForm({ id: null, work_date: todayDateOnly(), minutes: 0, reason: "" });
    setScheduleSaveAttempted(false);

    const assignment = data?.workPatternAssignment || null;
    const loadedEffectiveFrom = assignment?.effective_from || todayDateOnly();
    const loadedEffectiveTo = assignment?.effective_to || "";

    setPatternForm({
      template_id: assignment?.template_id ? String(assignment.template_id) : "",
      effective_from: loadedEffectiveFrom,
      effective_to: loadedEffectiveTo,
      cycle_start_date: assignment?.cycle_start_date || assignment?.effective_from || todayDateOnly(),

      // If the stored assignment happens to cover a whole number of weeks, show
      // that duration as a convenience.  If the dates are manually irregular,
      // keep the helper empty and preserve the exact effective_to date.
      assignment_duration_weeks: deriveWholeWeekDuration(loadedEffectiveFrom, loadedEffectiveTo),
    });
  }

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const data = await fetchVacationSettings(userId, { signal: ac.signal });
        applyLoadedBundle(data);
      } catch (e) {
        if (isRequestCanceled(e)) return;
        // eslint-disable-next-line no-console
        console.error("[VacationSettingsSection] ❌ load failed:", e);
        setError(t("leaves:vacationSettings.loadError"));
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
    
  }, [userId, t]);

  const scheduleDays = useMemo(() => normalizeScheduleDays(draftSchedule), [draftSchedule]);

  const totalWeeklyMinutes = useMemo(
    () => scheduleDays.reduce((acc, d) => acc + Number(d.minutes || 0), 0),
    [scheduleDays]
  );

  const workPatterns = useMemo(
    () => (Array.isArray(bundle?.workPatterns) ? bundle.workPatterns : []),
    [bundle]
  );

  const workScheduleOverrides = useMemo(
    () => (Array.isArray(bundle?.workScheduleOverrides) ? bundle.workScheduleOverrides : []),
    [bundle]
  );

  const persistedLeaveDayMinutes = useMemo(() => {
    const value = Number(bundle?.profile?.leave_day_minutes || 480);
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 480;
  }, [bundle?.profile?.leave_day_minutes]);

  const draftLeaveDayMinutes = useMemo(() => {
    const value = Number(draftProfile?.leave_day_minutes || 480);
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 480;
  }, [draftProfile?.leave_day_minutes]);

  const hasUnsavedLeaveDayNormChange = draftLeaveDayMinutes !== persistedLeaveDayMinutes;

  const leaveDayNormInputError = useMemo(() => {
    if (leaveDayNormMode !== "custom") return "";

    const minutes = hoursInputToMinutes(customLeaveDayHoursInput);
    if (minutes == null) return t("leaves:vacationSettings.leaveDayMinutesCustomInvalid");

    // Keep the business rule intentionally conservative.  This is a legal / HR
    // balance divisor, not a normal workday fallback.  Values outside 1h-12h
    // almost certainly indicate an input mistake and should not be persisted.
    if (minutes < 60 || minutes > 720) {
      return t("leaves:vacationSettings.leaveDayMinutesCustomRange");
    }

    return "";
  }, [customLeaveDayHoursInput, leaveDayNormMode, t]);

  const selectedPattern = useMemo(
    () => workPatterns.find((p) => String(p.id) === String(patternForm.template_id)) || null,
    [workPatterns, patternForm.template_id]
  );

  const selectedPatternDays = useMemo(
    () => normalizePatternDays(selectedPattern),
    [selectedPattern]
  );

  const scheduleValidationError = useMemo(() => {
    if (!draftSchedule?.effective_from) return t("leaves:vacationSettings.validation.effectiveFromRequired");

    const from = new Date(draftSchedule.effective_from);
    if (!Number.isFinite(from.getTime())) return t("leaves:vacationSettings.validation.effectiveFromInvalid");

    if (draftSchedule.effective_to) {
      const to = new Date(draftSchedule.effective_to);
      if (!Number.isFinite(to.getTime())) return t("leaves:vacationSettings.validation.effectiveToInvalid");
      if (to < from) return t("leaves:vacationSettings.validation.effectiveToBeforeFrom");
    }

    for (const d of scheduleDays) {
      const m = Number(d.minutes);
      if (!Number.isInteger(m) || m < 0 || m > 1440) {
        return t("leaves:vacationSettings.validation.minutesRange");
      }
    }

    return "";
  }, [draftSchedule, scheduleDays, t]);

  const patternValidationError = useMemo(() => {
    if (!patternForm.template_id) return "";

    const from = parseDateOnlyUtc(patternForm.effective_from);
    const cycle = parseDateOnlyUtc(patternForm.cycle_start_date);
    if (!from) return t("leaves:vacationSettings.validation.effectiveFromInvalid");
    if (!cycle) return t("leaves:vacationSettings.validation.cycleStartInvalid");

    if (patternForm.assignment_duration_weeks) {
      const weeks = Number(patternForm.assignment_duration_weeks);
      if (!Number.isInteger(weeks) || weeks <= 0 || weeks > 260) {
        return t("leaves:vacationSettings.validation.assignmentDurationInvalid");
      }
    }

    if (patternForm.effective_to) {
      const to = parseDateOnlyUtc(patternForm.effective_to);
      if (!to) return t("leaves:vacationSettings.validation.effectiveToInvalid");
      if (to < from) return t("leaves:vacationSettings.validation.effectiveToBeforeFrom");
    }

    return "";
  }, [patternForm, t]);

  const overrideValidationError = useMemo(() => {
    if (!overrideForm?.work_date) return t("leaves:vacationSettings.validation.overrideDateRequired");

    const date = parseDateOnlyUtc(overrideForm.work_date);
    if (!date) return t("leaves:vacationSettings.validation.overrideDateInvalid");

    const minutes = Number(overrideForm.minutes);
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
      return t("leaves:vacationSettings.validation.minutesRange");
    }

    return "";
  }, [overrideForm, t]);

  const setScheduleMinutes = (dow, minutes) => {
    const m = Number(minutes);
    const safe = Number.isFinite(m) ? Math.trunc(m) : 0;

    setDraftSchedule((prev) => {
      const normalized = normalizeScheduleDays(prev);
      return {
        ...(prev || {}),
        days: normalized.map((d) => (d.day_of_week === dow ? { ...d, minutes: safe } : d)),
      };
    });
  };

  const applyPresetMonFri8h = () => {
    setDraftSchedule((prev) => ({
      ...(prev || {}),
      days: ISO_DAYS.map((dow) => ({
        day_of_week: dow,
        minutes: dow >= 1 && dow <= 5 ? 480 : 0,
      })),
    }));
  };

  const applyPresetAllZero = () => {
    setDraftSchedule((prev) => ({
      ...(prev || {}),
      days: ISO_DAYS.map((dow) => ({ day_of_week: dow, minutes: 0 })),
    }));
  };

  const setPatternEffectiveFrom = (value) => {
    setPatternForm((prev) => {
      const next = { ...(prev || {}), effective_from: value };

      // Keep the helper truly helpful: when the administrator entered a
      // duration in weeks and then changes Effective From, we automatically
      // shift Effective To to the matching inclusive end date.
      if (next.assignment_duration_weeks) {
        next.effective_to = calculateEffectiveToFromDuration(value, next.assignment_duration_weeks);
      }

      return next;
    });
  };

  const setPatternDurationWeeks = (value) => {
    const normalized = normalizePositiveIntegerString(value);

    setPatternForm((prev) => {
      const next = { ...(prev || {}), assignment_duration_weeks: normalized };

      if (!normalized) {
        // Clearing duration does not clear Effective To.  Effective To may have
        // been maintained manually and remains the persisted business date.
        return next;
      }

      const calculatedTo = calculateEffectiveToFromDuration(next.effective_from, normalized);
      if (calculatedTo) next.effective_to = calculatedTo;

      return next;
    });
  };

  const setPatternEffectiveToManually = (value) => {
    setPatternForm((prev) => ({
      ...(prev || {}),
      effective_to: value,

      // Manual Effective To is allowed and should not fight the helper.
      // Clearing the duration makes it explicit that the end date was entered
      // directly and not auto-derived from a whole-week duration.
      assignment_duration_weeks: "",
    }));
  };

  const resetToLoaded = () => {
    setError("");
    setSuccess("");
    applyLoadedBundle(bundle || null);
  };

  const saveProfile = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");

    if (leaveDayNormInputError) {
      setError(leaveDayNormInputError);
      return;
    }

    setSavingProfile(true);

    const previousLeaveDayMinutes = persistedLeaveDayMinutes;
    const nextLeaveDayMinutes = draftLeaveDayMinutes;

    try {
      const payload = {
        country_code: draftProfile?.country_code || "PL",
        hire_date: draftProfile?.hire_date || null,
        termination_date: draftProfile?.termination_date || null,
        fte_percent: draftProfile?.fte_percent ?? "100.00",
        default_daily_minutes:
          draftProfile?.default_daily_minutes === "" || draftProfile?.default_daily_minutes == null
            ? null
            : Number(draftProfile.default_daily_minutes),
        default_working_days: normalizeDefaultWorkingDays(draftProfile?.default_working_days),
        leave_day_minutes: Number.isFinite(nextLeaveDayMinutes) && nextLeaveDayMinutes > 0
          ? Math.trunc(nextLeaveDayMinutes)
          : 480,
        annual_allowance_days: draftProfile?.annual_allowance_days ?? "0.00",
      };

      const updated = await upsertLeaveProfile(userId, payload);
      applyLoadedBundle(updated);

      if (previousLeaveDayMinutes !== payload.leave_day_minutes) {
        setShowLeaveDayNormRecalc(true);
        setRecalcPreview(null);
        setSuccess(t("leaves:vacationSettings.profileSavedRecalcRecommended"));
      } else {
        setSuccess(t("leaves:vacationSettings.profileSaved"));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ saveProfile failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.profileSaveError"));
    } finally {
      setSavingProfile(false);
    }
  };

  const loadRecalculationPreview = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");
    setLoadingRecalcPreview(true);

    try {
      const preview = await previewLeaveDayNormRecalculation(userId, recalcYear, {
        includePending: true,
      });
      setRecalcPreview(preview);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ loadRecalculationPreview failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.recalcPreviewError"));
    } finally {
      setLoadingRecalcPreview(false);
    }
  };

  const runLeaveDayNormRecalculation = async () => {
    if (isReadOnly) return;

    const ok = window.confirm(t("leaves:vacationSettings.recalcConfirm"));
    if (!ok) return;

    setError("");
    setSuccess("");
    setRunningRecalc(true);

    try {
      const result = await recalculateLeaveBalancesAfterDayNormChange(userId, {
        year: Number(recalcYear),
        includePending: true,
        reason: t("leaves:vacationSettings.recalcReasonDefault"),
      });
      setRecalcPreview(result);
      setSuccess(t("leaves:vacationSettings.recalcSuccess"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ runLeaveDayNormRecalculation failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.recalcRunError"));
    } finally {
      setRunningRecalc(false);
    }
  };

  const saveSchedule = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");
    setScheduleSaveAttempted(true);

    if (scheduleValidationError) {
      setError(scheduleValidationError);
      return;
    }

    setSavingSchedule(true);

    try {
      const payload = {
        effective_from: draftSchedule.effective_from,
        effective_to: draftSchedule.effective_to || null,
        days: normalizeScheduleDays(draftSchedule).map((d) => ({
          day_of_week: d.day_of_week,
          minutes: Number(d.minutes || 0),
        })),
      };

      const updated = await setWorkSchedule(userId, payload);
      applyLoadedBundle(updated);
      setSuccess(t("leaves:vacationSettings.scheduleSaved"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ saveSchedule failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.scheduleSaveError"));
    } finally {
      setSavingSchedule(false);
    }
  };

  const editOverride = (row) => {
    if (isReadOnly || !row) return;
    setError("");
    setSuccess("");
    setOverrideForm({
      id: row.id || null,
      work_date: row.work_date || todayDateOnly(),
      minutes: Number(row.minutes || 0),
      reason: row.reason || "",
    });
  };

  const resetOverrideForm = () => {
    setOverrideForm({ id: null, work_date: todayDateOnly(), minutes: 0, reason: "" });
  };

  const saveOverride = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");

    if (overrideValidationError) {
      setError(overrideValidationError);
      return;
    }

    setSavingOverride(true);

    try {
      const updated = await setWorkScheduleOverride(userId, {
        work_date: overrideForm.work_date,
        minutes: Number(overrideForm.minutes || 0),
        reason: String(overrideForm.reason || "").trim() || null,
      });

      applyLoadedBundle(updated);
      setSuccess(t("leaves:vacationSettings.overrideSaved"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ saveOverride failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.overrideSaveError"));
    } finally {
      setSavingOverride(false);
    }
  };

  const deleteOverride = async (row) => {
    if (isReadOnly || !row?.id) return;

    const ok = window.confirm(t("leaves:vacationSettings.overrideDeleteConfirm"));
    if (!ok) return;

    setError("");
    setSuccess("");
    setSavingOverride(true);

    try {
      const updated = await deleteWorkScheduleOverride(userId, row.id);
      applyLoadedBundle(updated);
      setSuccess(t("leaves:vacationSettings.overrideDeleted"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ deleteOverride failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.overrideDeleteError"));
    } finally {
      setSavingOverride(false);
    }
  };

  const savePatternAssignment = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");

    if (!patternForm.template_id) {
      setError(t("leaves:vacationSettings.validation.patternRequired"));
      return;
    }

    if (patternValidationError) {
      setError(patternValidationError);
      return;
    }

    setSavingPattern(true);

    try {
      const updated = await setWorkPatternAssignment(userId, {
        template_id: Number(patternForm.template_id),
        effective_from: patternForm.effective_from,
        effective_to: patternForm.effective_to || null,
        cycle_start_date: patternForm.cycle_start_date,
      });

      applyLoadedBundle(updated);
      setSuccess(t("leaves:vacationSettings.patternSaved"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ savePatternAssignment failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.patternSaveError"));
    } finally {
      setSavingPattern(false);
    }
  };

  const clearPattern = async () => {
    if (isReadOnly) return;
    setError("");
    setSuccess("");
    setSavingPattern(true);

    try {
      const updated = await clearWorkPatternAssignment(userId);
      applyLoadedBundle(updated);
      setSuccess(t("leaves:vacationSettings.patternCleared"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VacationSettingsSection] ❌ clearPattern failed:", e);
      setError(e?.response?.data?.message || t("leaves:vacationSettings.patternSaveError"));
    } finally {
      setSavingPattern(false);
    }
  };

  if (loading && !bundle) return <p>{t("leaves:vacationSettings.loading")}</p>;

  return (
    <div className="vac-section">
      <div className="section-header">
        <h2>{t("leaves:vacationSettings.title")}</h2>
        <p className="section-subtitle">{t("leaves:vacationSettings.subtitle")}</p>
      </div>

      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">{success}</p>}

      {!isReadOnly && (
        <div className="vac-actions">
          <button type="button" className="mode-btn" onClick={resetToLoaded} disabled={isBusy || !bundle}>
            {t("common:actions.reset")}
          </button>
        </div>
      )}

      <div className="vac-card">
        <div className="vac-card-title">{t("leaves:vacationSettings.profileTitle")}</div>
        <p className="section-subtitle">{t("leaves:vacationSettings.profileHelp")}</p>

        <div className="vac-grid">
          <div className="vac-field">
            <label>{t("leaves:vacationSettings.countryCode")}</label>
            <input
              type="text"
              value={draftProfile?.country_code || "PL"}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), country_code: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.hireDate")}</label>
            <input
              type="date"
              value={draftProfile?.hire_date || ""}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), hire_date: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.terminationDateOptional")}</label>
            <input
              type="date"
              value={draftProfile?.termination_date || ""}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), termination_date: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.ftePercent")}</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={draftProfile?.fte_percent ?? "100.00"}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), fte_percent: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.defaultDailyMinutesOptional")}</label>
            <input
              type="number"
              min="0"
              step="1"
              value={draftProfile?.default_daily_minutes ?? ""}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), default_daily_minutes: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>

          <div className="vac-field vac-field-leave-day-norm">
            <label>{t("leaves:vacationSettings.leaveDayMinutes")}</label>
            <select
              value={leaveDayNormMode}
              onChange={(e) => {
                const nextMode = e.target.value;
                setLeaveDayNormMode(nextMode);

                if (nextMode === "480" || nextMode === "420") {
                  const nextValue = Number(nextMode);

                  // Selecting a different leave-day norm is not just a cosmetic
                  // profile edit. Existing yearly summaries and already allocated
                  // request values may have been calculated with the previous norm.
                  // Therefore we show the recalculation callout immediately, even
                  // before the profile is saved. The actual preview/run buttons stay
                  // disabled until the profile value is persisted, because the backend
                  // recalculation must use the authoritative saved profile.
                  setDraftProfile((p) => ({ ...(p || {}), leave_day_minutes: nextValue }));
                  setCustomLeaveDayHoursInput(formatNormHours(nextValue));
                  setShowLeaveDayNormRecalc(true);
                  setRecalcPreview(null);
                  return;
                }

                // Custom mode keeps the current numeric value as the starting point.
                // The administrator can then enter any valid balance-day norm, e.g.
                // 6h, 7.5h or another special statutory / contractual value.
                setCustomLeaveDayHoursInput(formatNormHours(draftLeaveDayMinutes));
              }}
              disabled={savingProfile || isReadOnly}
            >
              <option value="480">{t("leaves:vacationSettings.leaveDayMinutes8h")}</option>
              <option value="420">{t("leaves:vacationSettings.leaveDayMinutes7h")}</option>
              <option value="custom">{t("leaves:vacationSettings.leaveDayMinutesCustom")}</option>
            </select>

            {leaveDayNormMode === "custom" && (
              <div className="vac-leave-day-custom-row">
                <label className="vac-inline-label" htmlFor={`leave-day-norm-custom-${userId}`}>
                  {t("leaves:vacationSettings.leaveDayMinutesCustomHours")}
                </label>
                <input
                  id={`leave-day-norm-custom-${userId}`}
                  type="number"
                  min="1"
                  max="12"
                  step="0.25"
                  value={customLeaveDayHoursInput}
                  onChange={(e) => {
                    const nextText = e.target.value;
                    const nextMinutes = hoursInputToMinutes(nextText);

                    setCustomLeaveDayHoursInput(nextText);

                    if (nextMinutes != null) {
                      setDraftProfile((p) => ({ ...(p || {}), leave_day_minutes: nextMinutes }));
                      setShowLeaveDayNormRecalc(true);
                      setRecalcPreview(null);
                    }
                  }}
                  disabled={savingProfile || isReadOnly}
                />
              </div>
            )}

            {leaveDayNormInputError ? (
              <small className="vac-field-error">{leaveDayNormInputError}</small>
            ) : (
              <small>{t("leaves:vacationSettings.leaveDayMinutesHelp", { hours: formatNormHours(draftLeaveDayMinutes) })}</small>
            )}
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.defaultWorkingDays")}</label>
            <input
              type="text"
              value={draftProfile?.default_working_days || "1,2,3,4,5"}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), default_working_days: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
            <small>{t("leaves:vacationSettings.defaultWorkingDaysHelp")}</small>
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.annualAllowanceDays")}</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={draftProfile?.annual_allowance_days ?? "0.00"}
              onChange={(e) => setDraftProfile((p) => ({ ...(p || {}), annual_allowance_days: e.target.value }))}
              disabled={savingProfile || isReadOnly}
            />
          </div>
        </div>

        {!isReadOnly && (
          <div className="vac-save-row">
            <button type="button" className="primary" onClick={saveProfile} disabled={savingProfile || Boolean(leaveDayNormInputError)}>
              {savingProfile ? t("leaves:vacationSettings.saving") : t("leaves:vacationSettings.saveProfile")}
            </button>
          </div>
        )}

        {!isReadOnly && showLeaveDayNormRecalc && (
          <div className="vac-recalc-callout">
            <div className="vac-recalc-title">{t("leaves:vacationSettings.recalcCalloutTitle")}</div>
            <p>{t("leaves:vacationSettings.recalcCalloutHelp")}</p>
            {hasUnsavedLeaveDayNormChange && (
              <div className="vac-recalc-unsaved-note">
                {t("leaves:vacationSettings.recalcSaveProfileFirst", {
                  from: formatNormHours(persistedLeaveDayMinutes),
                  to: formatNormHours(draftLeaveDayMinutes),
                })}
              </div>
            )}

            <div className="vac-inline-actions">
              <label className="vac-recalc-year">
                <span>{t("leaves:vacationSettings.recalcYear")}</span>
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  step="1"
                  value={recalcYear}
                  onChange={(e) => setRecalcYear(e.target.value)}
                  disabled={loadingRecalcPreview || runningRecalc}
                />
              </label>
              <button type="button" className="mode-btn" onClick={loadRecalculationPreview} disabled={hasUnsavedLeaveDayNormChange || loadingRecalcPreview || runningRecalc}>
                {loadingRecalcPreview ? t("leaves:vacationSettings.loadingRecalcPreview") : t("leaves:vacationSettings.recalcPreviewButton")}
              </button>
              <button type="button" className="primary" onClick={runLeaveDayNormRecalculation} disabled={hasUnsavedLeaveDayNormChange || runningRecalc}>
                {runningRecalc ? t("leaves:vacationSettings.recalcRunning") : t("leaves:vacationSettings.recalcRunButton")}
              </button>
            </div>

            {recalcPreview && Array.isArray(recalcPreview.rows) && (
              <div className="vac-table-wrapper">
                <table className="tiers-table vac-recalc-table">
                  <thead>
                    <tr>
                      <th>{t("leaves:vacationSettings.recalcLeaveType")}</th>
                      <th>{t("leaves:vacationSettings.recalcBefore")}</th>
                      <th>{t("leaves:vacationSettings.recalcAfter")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recalcPreview.rows.slice(0, 12).map((row) => {
                      const leaveType = (bundle?.leaveTypes || []).find((lt) => Number(lt.id) === Number(row.leave_type_id));
                      const label = leaveType?.name_pl || leaveType?.name_en || leaveType?.code || row.leave_type_id;
                      return (
                        <tr key={row.leave_type_id}>
                          <td style={{ fontWeight: 800 }}>{label}</td>
                          <td>
                            {row.before
                              ? `${t("leaves:vacationSettings.recalcTaken")}: ${formatRecalcValue(row.before.taken)} · ${t("leaves:vacationSettings.recalcPlanned")}: ${formatRecalcValue(row.before.planned)} · ${t("leaves:vacationSettings.recalcAvailable")}: ${formatRecalcValue(row.before.available)}`
                              : t("leaves:vacationSettings.recalcNoCachedSummary")}
                          </td>
                          <td>
                            {`${t("leaves:vacationSettings.recalcTaken")}: ${formatRecalcValue(row.after?.taken)} · ${t("leaves:vacationSettings.recalcPlanned")}: ${formatRecalcValue(row.after?.planned)} · ${t("leaves:vacationSettings.recalcAvailable")}: ${formatRecalcValue(row.after?.available)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {recalcPreview.rows.length > 12 && (
                  <p className="vac-muted">{t("leaves:vacationSettings.recalcMoreRows", { count: recalcPreview.rows.length - 12 })}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="vac-card">
        <div className="vac-card-title">{t("leaves:vacationSettings.patternTitle")}</div>
        <p className="section-subtitle">{t("leaves:vacationSettings.patternHelp")}</p>

        <div className="vac-grid">
          <div className="vac-field">
            <label>{t("leaves:vacationSettings.pattern")}</label>
            <select
              value={patternForm.template_id}
              onChange={(e) => setPatternForm((p) => ({ ...(p || {}), template_id: e.target.value }))}
              disabled={savingPattern || isReadOnly}
            >
              <option value="">{t("leaves:vacationSettings.noPattern")}</option>
              {workPatterns.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.effectiveFrom")}</label>
            <input
              type="date"
              value={patternForm.effective_from || ""}
              onChange={(e) => setPatternEffectiveFrom(e.target.value)}
              disabled={savingPattern || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.effectiveToOptional")}</label>
            <input
              type="date"
              value={patternForm.effective_to || ""}
              onChange={(e) => setPatternEffectiveToManually(e.target.value)}
              disabled={savingPattern || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.assignmentDurationWeeksOptional")}</label>
            <input
              type="number"
              min="1"
              max="260"
              step="1"
              value={patternForm.assignment_duration_weeks || ""}
              onChange={(e) => setPatternDurationWeeks(e.target.value)}
              disabled={savingPattern || isReadOnly || !patternForm.effective_from}
            />
            <small>{t("leaves:vacationSettings.assignmentDurationHelp")}</small>
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.cycleStartDate")}</label>
            <input
              type="date"
              value={patternForm.cycle_start_date || ""}
              onChange={(e) => setPatternForm((p) => ({ ...(p || {}), cycle_start_date: e.target.value }))}
              disabled={savingPattern || isReadOnly}
            />
            <small>{t("leaves:vacationSettings.cycleStartHelp")}</small>
          </div>
        </div>

        {selectedPattern && selectedPatternDays.length > 0 && (
          <div className="vac-table-wrapper" style={{ marginTop: 12 }}>
            <table className="tiers-table">
              <thead>
                <tr>
                  <th>{t("leaves:vacationSettings.week")}</th>
                  <th>{t("leaves:vacationSettings.day")}</th>
                  <th>{t("leaves:vacationSettings.minutes")}</th>
                  <th>{t("leaves:vacationSettings.hours")}</th>
                </tr>
              </thead>
              <tbody>
                {selectedPatternDays.map((d) => (
                  <tr key={`${d.week_index}-${d.day_of_week}`}>
                    <td>{d.week_index}</td>
                    <td style={{ fontWeight: 700 }}>{dayLabels[d.day_of_week - 1]}</td>
                    <td>{d.minutes}</td>
                    <td>{(Number(d.minutes || 0) / 60).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {patternValidationError && <p className="error-message">{patternValidationError}</p>}

        {!isReadOnly && (
          <div className="vac-save-row">
            <button type="button" className="mode-btn" onClick={clearPattern} disabled={savingPattern}>
              {t("leaves:vacationSettings.clearPattern")}
            </button>
            <button type="button" className="primary" onClick={savePatternAssignment} disabled={savingPattern}>
              {savingPattern ? t("leaves:vacationSettings.saving") : t("leaves:vacationSettings.savePattern")}
            </button>
          </div>
        )}
      </div>

      <div className="vac-card">
        <div className="vac-card-title">{t("leaves:vacationSettings.overrideTitle")}</div>
        <p className="section-subtitle">{t("leaves:vacationSettings.overrideHelp")}</p>

        <div className="vac-grid">
          <div className="vac-field">
            <label>{t("leaves:vacationSettings.overrideDate")}</label>
            <input
              type="date"
              value={overrideForm.work_date || ""}
              onChange={(e) => setOverrideForm((p) => ({ ...(p || {}), work_date: e.target.value }))}
              disabled={savingOverride || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.overrideMinutes")}</label>
            <input
              type="number"
              min="0"
              max="1440"
              step="1"
              value={overrideForm.minutes ?? 0}
              onChange={(e) => setOverrideForm((p) => ({ ...(p || {}), minutes: e.target.value }))}
              disabled={savingOverride || isReadOnly}
            />
            <small>{t("leaves:vacationSettings.overrideMinutesHelp")}</small>
          </div>

          <div className="vac-field vac-field-wide">
            <label>{t("leaves:vacationSettings.overrideReason")}</label>
            <input
              type="text"
              maxLength="255"
              value={overrideForm.reason || ""}
              onChange={(e) => setOverrideForm((p) => ({ ...(p || {}), reason: e.target.value }))}
              disabled={savingOverride || isReadOnly}
            />
          </div>
        </div>

        {overrideValidationError && !isReadOnly && <p className="error-message">{overrideValidationError}</p>}

        {!isReadOnly && (
          <div className="vac-save-row vac-save-row-gap">
            <button type="button" className="mode-btn" onClick={resetOverrideForm} disabled={savingOverride}>
              {t("leaves:vacationSettings.newOverride")}
            </button>
            <button type="button" className="primary" onClick={saveOverride} disabled={savingOverride}>
              {savingOverride ? t("leaves:vacationSettings.saving") : t("leaves:vacationSettings.saveOverride")}
            </button>
          </div>
        )}

        <div className="vac-table-wrapper">
          <table className="tiers-table">
            <thead>
              <tr>
                <th>{t("leaves:vacationSettings.overrideDate")}</th>
                <th>{t("leaves:vacationSettings.minutes")}</th>
                <th>{t("leaves:vacationSettings.hours")}</th>
                <th>{t("leaves:vacationSettings.overrideReason")}</th>
                {!isReadOnly && <th>{t("leaves:vacationSettings.overrideActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {workScheduleOverrides.length === 0 ? (
                <tr>
                  <td colSpan={isReadOnly ? 4 : 5} className="vac-empty-cell">
                    {t("leaves:vacationSettings.noOverrides")}
                  </td>
                </tr>
              ) : (
                workScheduleOverrides.map((row) => (
                  <tr key={row.id || row.work_date}>
                    <td style={{ fontWeight: 700 }}>{row.work_date}</td>
                    <td>{Number(row.minutes || 0)}</td>
                    <td>{formatMinutesAsHours(row.minutes)}</td>
                    <td>{row.reason || "—"}</td>
                    {!isReadOnly && (
                      <td>
                        <div className="vac-inline-actions">
                          <button type="button" className="mode-btn" onClick={() => editOverride(row)} disabled={savingOverride}>
                            {t("common:actions.edit")}
                          </button>
                          <button type="button" className="mode-btn danger" onClick={() => deleteOverride(row)} disabled={savingOverride}>
                            {t("common:actions.delete")}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vac-card">
        <div className="vac-card-title">{t("leaves:vacationSettings.workScheduleTitle")}</div>
        <p className="section-subtitle">{t("leaves:vacationSettings.workScheduleHelp")}</p>

        <div className="vac-grid">
          <div className="vac-field">
            <label>{t("leaves:vacationSettings.effectiveFrom")}</label>
            <input
              type="date"
              value={draftSchedule?.effective_from || ""}
              onChange={(e) => setDraftSchedule((s) => ({ ...(s || {}), effective_from: e.target.value }))}
              disabled={savingSchedule || isReadOnly}
            />
          </div>

          <div className="vac-field">
            <label>{t("leaves:vacationSettings.effectiveToOptional")}</label>
            <input
              type="date"
              value={draftSchedule?.effective_to || ""}
              onChange={(e) => setDraftSchedule((s) => ({ ...(s || {}), effective_to: e.target.value }))}
              disabled={savingSchedule || isReadOnly}
            />
          </div>
        </div>

        {!isReadOnly && (
          <div className="vac-actions">
            <button type="button" className="mode-btn" onClick={applyPresetMonFri8h} disabled={savingSchedule}>
              {t("leaves:vacationSettings.presetMonFri8h")}
            </button>

            <button type="button" className="mode-btn" onClick={applyPresetAllZero} disabled={savingSchedule}>
              {t("leaves:vacationSettings.presetAllZero")}
            </button>
          </div>
        )}

        {scheduleSaveAttempted && scheduleValidationError && (
          <p className="error-message">{scheduleValidationError}</p>
        )}

        <div className="vac-table-wrapper">
          <table className="tiers-table">
            <thead>
              <tr>
                <th>{t("leaves:vacationSettings.day")}</th>
                <th>{t("leaves:vacationSettings.minutes")}</th>
                <th>{t("leaves:vacationSettings.hours")}</th>
              </tr>
            </thead>
            <tbody>
              {scheduleDays.map((d) => (
                <tr key={d.day_of_week}>
                  <td style={{ fontWeight: 700 }}>{dayLabels[d.day_of_week - 1]}</td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="1440"
                      step="1"
                      value={d.minutes}
                      onChange={(e) => setScheduleMinutes(d.day_of_week, e.target.value)}
                      disabled={savingSchedule || isReadOnly}
                    />
                  </td>
                  <td>{(Number(d.minutes) / 60).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="tiers-hint">
          {t("leaves:vacationSettings.weeklyTotal", {
            minutes: totalWeeklyMinutes,
            hours: (totalWeeklyMinutes / 60).toFixed(2),
          })}
        </p>

        {!isReadOnly && (
          <div className="vac-save-row">
            <button type="button" className="primary" onClick={saveSchedule} disabled={savingSchedule}>
              {savingSchedule ? t("leaves:vacationSettings.saving") : t("leaves:vacationSettings.saveSchedule")}
            </button>
          </div>
        )}
      </div>

      <div className="vac-breakout-wide">
        <VacationBalancesSection userId={userId} leaveTypes={bundle?.leaveTypes || []} readOnly={isReadOnly} />
      </div>
    </div>
  );
}

